import React, { useCallback, useEffect, useState } from 'react';
import {
  Box,
  Button,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  CircularProgress,
  Chip,
  Tooltip,
  LinearProgress,
  MenuItem,
  Select,
  FormControl,
  InputLabel,
  Grid,
  Card,
  CardContent,
  Alert,
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import VisibilityIcon from '@mui/icons-material/Visibility';
import api from '../services/api';
import { Project, ProjectFinancials } from '../types';
import { useAuth } from '../contexts/AuthContext';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';
import { format } from 'date-fns';
import { XAxis, YAxis, CartesianGrid, Tooltip as RechartsTooltip, Legend, ResponsiveContainer, LineChart, Line } from 'recharts';
import ProjectContractorICVSection from '../components/projects/ProjectContractorICVSection';

const Projects: React.FC = () => {
  const [projects, setProjects] = useState<Project[]>([]);
  const [loading, setLoading] = useState(true);
  const [open, setOpen] = useState(false);
  const [detailOpen, setDetailOpen] = useState(false);
  const [selectedProject, setSelectedProject] = useState<Project | null>(null);
  const [projectFinancials, setProjectFinancials] = useState<ProjectFinancials | null>(null);
  const [editing, setEditing] = useState<Project | null>(null);
  const [budgetStatusFilter, setBudgetStatusFilter] = useState<string>('all');
  const [formData, setFormData] = useState({
    name: '',
    description: '',
    clientName: '',
    startDate: null as Date | null,
    endDate: null as Date | null,
    totalBudget: '',
    currency: 'USD',
    budgetStartDate: null as Date | null,
    budgetEndDate: null as Date | null,
    budgetStatus: 'Active',
    isActive: true,
  });
  const { user } = useAuth();

  const fetchProjects = useCallback(async () => {
    try {
      setLoading(true);
      const response = await api.get('/project-financials');
      setProjects(response.data);
    } catch (error) {
      console.error('Failed to fetch projects:', error);
      // Fallback to regular projects endpoint
      const fallbackResponse = await api.get('/projects');
      setProjects(fallbackResponse.data);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchProjects();
  }, [fetchProjects]);

  const fetchProjectFinancials = async (projectId: string) => {
    try {
      const response = await api.get(`/project-financials/${projectId}`);
      setProjectFinancials(response.data);
    } catch (error) {
      console.error('Failed to fetch project financials:', error);
    }
  };

  const handleOpen = (project?: Project) => {
    if (project) {
      setEditing(project);
      setFormData({
        name: project.name,
        description: project.description || '',
        clientName: project.clientName || '',
        startDate: project.startDate ? new Date(project.startDate) : null,
        endDate: project.endDate ? new Date(project.endDate) : null,
        totalBudget: project.totalBudget?.toString() || '',
        currency: project.currency || 'USD',
        budgetStartDate: project.budgetStartDate ? new Date(project.budgetStartDate) : null,
        budgetEndDate: project.budgetEndDate ? new Date(project.budgetEndDate) : null,
        budgetStatus: project.budgetStatus || 'Active',
        isActive: project.isActive,
      });
    } else {
      setEditing(null);
      setFormData({
        name: '',
        description: '',
        clientName: '',
        startDate: null,
        endDate: null,
        totalBudget: '',
        currency: 'USD',
        budgetStartDate: null,
        budgetEndDate: null,
        budgetStatus: 'Active',
        isActive: true,
      });
    }
    setOpen(true);
  };

  const handleClose = () => {
    setOpen(false);
    setEditing(null);
  };

  const handleViewDetails = async (project: Project) => {
    setSelectedProject(project);
    await fetchProjectFinancials(project.id);
    setDetailOpen(true);
  };

  const handleSubmit = async () => {
    try {
      const data = {
        ...formData,
        startDate: formData.startDate?.toISOString(),
        endDate: formData.endDate?.toISOString(),
        budgetStartDate: formData.budgetStartDate?.toISOString(),
        budgetEndDate: formData.budgetEndDate?.toISOString(),
        totalBudget: formData.totalBudget ? parseFloat(formData.totalBudget) : null,
      };

      if (editing) {
        await api.put(`/projects/${editing.id}`, data);
      } else {
        await api.post('/projects', data);
      }

      fetchProjects();
      handleClose();
    } catch (error: any) {
      alert(error.response?.data?.message || 'Failed to save project');
    }
  };

  const handleDelete = async (id: string) => {
    if (!window.confirm('Are you sure you want to delete this project?')) {
      return;
    }

    try {
      await api.delete(`/projects/${id}`);
      fetchProjects();
    } catch (error: any) {
      alert(error.response?.data?.message || 'Failed to delete project');
    }
  };

  const exportToExcel = async () => {
    try {
      const params: any = {};
      if (budgetStatusFilter !== 'all') {
        params.budgetStatus = budgetStatusFilter;
      }

      const response = await api.get('/export/project-financials', {
        params,
        responseType: 'blob',
      });

      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `project-financial-summary-${new Date().toISOString().split('T')[0]}.xlsx`);
      document.body.appendChild(link);
      link.click();
      link.remove();
    } catch (error) {
      console.error('Export failed:', error);
    }
  };

  const getBudgetStatusColor = (utilization: number, status?: string) => {
    if (utilization > 100) return 'error';
    if (utilization >= 90) return 'warning';
    if (utilization >= 70) return 'info';
    return 'success';
  };

  const getBudgetStatusLabel = (project: Project) => {
    if (project.calculatedBudgetStatus) {
      return project.calculatedBudgetStatus;
    }
    if (project.financials) {
      const util = project.financials.budgetUtilization;
      if (util > 100) return 'Over Budget';
      if (util >= 100) return 'At Budget';
      if (util >= 90) return 'Near Budget';
    }
    return project.budgetStatus || 'Active';
  };

  const filteredProjects = projects.filter(project => {
    if (budgetStatusFilter === 'all') return true;
    const status = getBudgetStatusLabel(project);
    if (budgetStatusFilter === 'under') return project.financials && project.financials.budgetUtilization < 70;
    if (budgetStatusFilter === 'on-track') return project.financials && project.financials.budgetUtilization >= 70 && project.financials.budgetUtilization < 90;
    if (budgetStatusFilter === 'near') return project.financials && project.financials.budgetUtilization >= 90 && project.financials.budgetUtilization <= 100;
    if (budgetStatusFilter === 'over') return project.financials && project.financials.budgetUtilization > 100;
    return status === budgetStatusFilter;
  });

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="400px">
        <CircularProgress />
      </Box>
    );
  }

  const isAdmin = user?.role === 'ADMIN';

  return (
    <Box>
      <Box display="flex" justifyContent="space-between" alignItems="center" mb={3}>
        <Typography variant="h4">Projects</Typography>
        <Box>
          <FormControl size="small" sx={{ minWidth: 200, mr: 1 }}>
            <InputLabel>Budget Status</InputLabel>
            <Select
              value={budgetStatusFilter}
              label="Budget Status"
              onChange={(e) => setBudgetStatusFilter(e.target.value)}
            >
              <MenuItem value="all">All Projects</MenuItem>
              <MenuItem value="under">Under Budget (&lt;70%)</MenuItem>
              <MenuItem value="on-track">On Track (70-90%)</MenuItem>
              <MenuItem value="near">Near Budget (90-100%)</MenuItem>
              <MenuItem value="over">Over Budget</MenuItem>
            </Select>
          </FormControl>
          <Button variant="outlined" onClick={exportToExcel} sx={{ mr: 1 }}>
            Export Financials
          </Button>
          {isAdmin && (
            <Button variant="contained" startIcon={<AddIcon />} onClick={() => handleOpen()}>
              Add Project
            </Button>
          )}
        </Box>
      </Box>

      <TableContainer component={Paper}>
        <Table>
          <TableHead>
            <TableRow>
              <TableCell>Project Name</TableCell>
              <TableCell>Client</TableCell>
              <TableCell>Total Budget</TableCell>
              <TableCell>Amount Charged</TableCell>
              <TableCell>Remaining</TableCell>
              <TableCell>Utilization</TableCell>
              <TableCell>Budget Status</TableCell>
              <TableCell>Status</TableCell>
              {isAdmin && <TableCell align="right">Actions</TableCell>}
            </TableRow>
          </TableHead>
          <TableBody>
            {filteredProjects.map((project) => {
              const financials = project.financials;
              const utilization = financials?.budgetUtilization || 0;
              const statusColor = getBudgetStatusColor(utilization);

              return (
                <TableRow key={project.id} sx={{ cursor: 'pointer' }} onClick={() => handleViewDetails(project)}>
                  <TableCell>
                    <Box>
                      <Typography variant="body2" fontWeight="bold">{project.name}</Typography>
                      {project.description && (
                        <Typography variant="caption" color="text.secondary">
                          {project.description.substring(0, 50)}...
                        </Typography>
                      )}
                    </Box>
                  </TableCell>
                  <TableCell>{project.clientName || '-'}</TableCell>
                  <TableCell>
                    {financials ? (
                      <Typography>
                        {financials.currency} {financials.totalBudget.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                      </Typography>
                    ) : (
                      project.totalBudget ? `${project.currency || 'USD'} ${project.totalBudget.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}` : '-'
                    )}
                  </TableCell>
                  <TableCell>
                    {financials ? (
                      <Typography>
                        {financials.currency} {financials.totalCharged.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                      </Typography>
                    ) : '-'}
                  </TableCell>
                  <TableCell>
                    {financials ? (
                      <Typography color={financials.remainingBudget < 0 ? 'error' : 'inherit'}>
                        {financials.currency} {financials.remainingBudget.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                      </Typography>
                    ) : '-'}
                  </TableCell>
                  <TableCell>
                    {financials ? (
                      <Box>
                        <Box display="flex" alignItems="center" gap={1}>
                          <Box flex={1}>
                            <LinearProgress
                              variant="determinate"
                              value={Math.min(utilization, 100)}
                              color={statusColor as any}
                              sx={{ height: 8, borderRadius: 4 }}
                            />
                          </Box>
                          <Typography variant="body2" sx={{ minWidth: 50 }}>
                            {utilization.toFixed(1)}%
                          </Typography>
                        </Box>
                      </Box>
                    ) : '-'}
                  </TableCell>
                  <TableCell>
                    <Chip
                      label={getBudgetStatusLabel(project)}
                      color={statusColor as any}
                      size="small"
                    />
                  </TableCell>
                  <TableCell>
                    <Chip
                      label={project.isActive ? 'Active' : 'Inactive'}
                      color={project.isActive ? 'success' : 'default'}
                      size="small"
                    />
                  </TableCell>
                  {isAdmin && (
                    <TableCell align="right" onClick={(e) => e.stopPropagation()}>
                      <Tooltip title="View Details">
                        <IconButton size="small" onClick={() => handleViewDetails(project)}>
                          <VisibilityIcon />
                        </IconButton>
                      </Tooltip>
                      <Tooltip title="Edit">
                        <IconButton size="small" onClick={() => handleOpen(project)}>
                          <EditIcon />
                        </IconButton>
                      </Tooltip>
                      <Tooltip title="Delete">
                        <IconButton size="small" onClick={() => handleDelete(project.id)}>
                          <DeleteIcon />
                        </IconButton>
                      </Tooltip>
                    </TableCell>
                  )}
                </TableRow>
              );
            })}
          </TableBody>
        </Table>
      </TableContainer>

      {/* Project Detail Dialog */}
      <Dialog open={detailOpen} onClose={() => setDetailOpen(false)} maxWidth="lg" fullWidth>
        <DialogTitle>
          {selectedProject?.name} - Financial Details
        </DialogTitle>
        <DialogContent>
          {projectFinancials && (
            <Box>
              <Grid container spacing={3} sx={{ mb: 3 }}>
                <Grid item xs={12} md={3}>
                  <Card>
                    <CardContent>
                      <Typography color="textSecondary" gutterBottom>Total Budget</Typography>
                      <Typography variant="h5">
                        {projectFinancials.financials.currency} {projectFinancials.financials.totalBudget.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                      </Typography>
                    </CardContent>
                  </Card>
                </Grid>
                <Grid item xs={12} md={3}>
                  <Card>
                    <CardContent>
                      <Typography color="textSecondary" gutterBottom>Amount Charged</Typography>
                      <Typography variant="h5">
                        {projectFinancials.financials.currency} {projectFinancials.financials.totalCharged.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                      </Typography>
                    </CardContent>
                  </Card>
                </Grid>
                <Grid item xs={12} md={3}>
                  <Card>
                    <CardContent>
                      <Typography color="textSecondary" gutterBottom>Remaining Budget</Typography>
                      <Typography variant="h5" color={projectFinancials.financials.remainingBudget < 0 ? 'error' : 'inherit'}>
                        {projectFinancials.financials.currency} {projectFinancials.financials.remainingBudget.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                      </Typography>
                    </CardContent>
                  </Card>
                </Grid>
                <Grid item xs={12} md={3}>
                  <Card>
                    <CardContent>
                      <Typography color="textSecondary" gutterBottom>Utilization</Typography>
                      <Typography variant="h5">
                        {projectFinancials.financials.budgetUtilization.toFixed(1)}%
                      </Typography>
                      <LinearProgress
                        variant="determinate"
                        value={Math.min(projectFinancials.financials.budgetUtilization, 100)}
                        color={getBudgetStatusColor(projectFinancials.financials.budgetUtilization) as any}
                        sx={{ mt: 1, height: 8, borderRadius: 4 }}
                      />
                    </CardContent>
                  </Card>
                </Grid>
              </Grid>

              {projectFinancials.financials.budgetUtilization >= 80 && (
                <Alert severity={projectFinancials.financials.budgetUtilization > 100 ? 'error' : 'warning'} sx={{ mb: 2 }}>
                  {projectFinancials.financials.budgetUtilization > 100
                    ? '⚠️ Project has exceeded budget!'
                    : '⚠️ Project has reached ' + projectFinancials.financials.budgetUtilization.toFixed(1) + '% of budget'}
                </Alert>
              )}

              <Grid container spacing={3}>
                <Grid item xs={12} md={6}>
                  <Paper sx={{ p: 2 }}>
                    <Typography variant="h6" gutterBottom>Breakdown by Contractor</Typography>
                    <TableContainer>
                      <Table size="small">
                        <TableHead>
                          <TableRow>
                            <TableCell>Contractor</TableCell>
                            <TableCell align="right">Hours</TableCell>
                            <TableCell align="right">Charged</TableCell>
                            <TableCell align="right">Unbilled</TableCell>
                          </TableRow>
                        </TableHead>
                        <TableBody>
                          {projectFinancials.breakdown.byContractor.map((contractor) => (
                            <TableRow key={contractor.contractorId}>
                              <TableCell>{contractor.contractorName}</TableCell>
                              <TableCell align="right">{contractor.totalHours.toFixed(2)}</TableCell>
                              <TableCell align="right">
                                {projectFinancials.financials.currency} {contractor.totalCharged.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                              </TableCell>
                              <TableCell align="right">
                                {projectFinancials.financials.currency} {contractor.unbilledValue.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                              </TableCell>
                            </TableRow>
                          ))}
                        </TableBody>
                      </Table>
                    </TableContainer>
                  </Paper>
                </Grid>
                <Grid item xs={12} md={6}>
                  <Paper sx={{ p: 2 }}>
                    <Typography variant="h6" gutterBottom>Budget Consumption Over Time</Typography>
                    <ResponsiveContainer width="100%" height={300}>
                      <LineChart data={projectFinancials.breakdown.byMonth}>
                        <CartesianGrid strokeDasharray="3 3" />
                        <XAxis dataKey="month" />
                        <YAxis />
                        <RechartsTooltip />
                        <Legend />
                        <Line type="monotone" dataKey="charged" stroke="#8884d8" name="Amount Charged" />
                      </LineChart>
                    </ResponsiveContainer>
                  </Paper>
                </Grid>
                {selectedProject && (
                  <Grid item xs={12}>
                    <ProjectContractorICVSection project={selectedProject} />
                  </Grid>
                )}
                <Grid item xs={12}>
                  <Paper sx={{ p: 2 }}>
                    <Typography variant="h6" gutterBottom>Invoices</Typography>
                    <TableContainer>
                      <Table size="small">
                        <TableHead>
                          <TableRow>
                            <TableCell>Invoice Number</TableCell>
                            <TableCell>Status</TableCell>
                            <TableCell align="right">Invoice Total</TableCell>
                            <TableCell align="right">Project Amount</TableCell>
                            <TableCell>Submission Date</TableCell>
                          </TableRow>
                        </TableHead>
                        <TableBody>
                          {projectFinancials.invoices.map((invoice) => (
                            <TableRow key={invoice.id}>
                              <TableCell>{invoice.invoiceNumber}</TableCell>
                              <TableCell>
                                <Chip label={invoice.status} size="small" />
                              </TableCell>
                              <TableCell align="right">
                                {projectFinancials.financials.currency} {invoice.total.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                              </TableCell>
                              <TableCell align="right">
                                {projectFinancials.financials.currency} {invoice.projectAmount.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                              </TableCell>
                              <TableCell>
                                {invoice.submissionDate ? format(new Date(invoice.submissionDate), 'MMM dd, yyyy') : '-'}
                              </TableCell>
                            </TableRow>
                          ))}
                        </TableBody>
                      </Table>
                    </TableContainer>
                  </Paper>
                </Grid>
              </Grid>
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDetailOpen(false)}>Close</Button>
        </DialogActions>
      </Dialog>

      {/* Create/Edit Project Dialog */}
      <Dialog open={open} onClose={handleClose} maxWidth="md" fullWidth>
        <DialogTitle>{editing ? 'Edit Project' : 'Add Project'}</DialogTitle>
        <DialogContent>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, mt: 1 }}>
            <TextField
              label="Project Name"
              value={formData.name}
              onChange={(e) => setFormData({ ...formData, name: e.target.value })}
              fullWidth
              required
            />
            <TextField
              label="Client Name"
              value={formData.clientName}
              onChange={(e) => setFormData({ ...formData, clientName: e.target.value })}
              fullWidth
            />
            <TextField
              label="Description"
              value={formData.description}
              onChange={(e) => setFormData({ ...formData, description: e.target.value })}
              fullWidth
              multiline
              rows={3}
            />
            <Grid container spacing={2}>
              <Grid item xs={6}>
                <LocalizationProvider dateAdapter={AdapterDateFns}>
                  <DatePicker
                    label="Start Date"
                    value={formData.startDate}
                    onChange={(date) => setFormData({ ...formData, startDate: date })}
                    slotProps={{ textField: { fullWidth: true } }}
                  />
                </LocalizationProvider>
              </Grid>
              <Grid item xs={6}>
                <LocalizationProvider dateAdapter={AdapterDateFns}>
                  <DatePicker
                    label="End Date"
                    value={formData.endDate}
                    onChange={(date) => setFormData({ ...formData, endDate: date })}
                    slotProps={{ textField: { fullWidth: true } }}
                  />
                </LocalizationProvider>
              </Grid>
            </Grid>
            <Typography variant="h6" sx={{ mt: 2 }}>Budget Information</Typography>
            <Grid container spacing={2}>
              <Grid item xs={6}>
                <TextField
                  label="Total Budget"
                  type="number"
                  value={formData.totalBudget}
                  onChange={(e) => setFormData({ ...formData, totalBudget: e.target.value })}
                  fullWidth
                  inputProps={{ step: '0.01', min: '0' }}
                />
              </Grid>
              <Grid item xs={6}>
                <FormControl fullWidth>
                  <InputLabel>Currency</InputLabel>
                  <Select
                    value={formData.currency}
                    label="Currency"
                    onChange={(e) => setFormData({ ...formData, currency: e.target.value })}
                  >
                    <MenuItem value="USD">USD</MenuItem>
                    <MenuItem value="EUR">EUR</MenuItem>
                    <MenuItem value="GBP">GBP</MenuItem>
                    <MenuItem value="CAD">CAD</MenuItem>
                  </Select>
                </FormControl>
              </Grid>
            </Grid>
            <Grid container spacing={2}>
              <Grid item xs={6}>
                <LocalizationProvider dateAdapter={AdapterDateFns}>
                  <DatePicker
                    label="Budget Start Date"
                    value={formData.budgetStartDate}
                    onChange={(date) => setFormData({ ...formData, budgetStartDate: date })}
                    slotProps={{ textField: { fullWidth: true } }}
                  />
                </LocalizationProvider>
              </Grid>
              <Grid item xs={6}>
                <LocalizationProvider dateAdapter={AdapterDateFns}>
                  <DatePicker
                    label="Budget End Date"
                    value={formData.budgetEndDate}
                    onChange={(date) => setFormData({ ...formData, budgetEndDate: date })}
                    slotProps={{ textField: { fullWidth: true } }}
                  />
                </LocalizationProvider>
              </Grid>
            </Grid>
            <FormControl fullWidth>
              <InputLabel>Budget Status</InputLabel>
              <Select
                value={formData.budgetStatus}
                label="Budget Status"
                onChange={(e) => setFormData({ ...formData, budgetStatus: e.target.value })}
              >
                <MenuItem value="Active">Active</MenuItem>
                <MenuItem value="Completed">Completed</MenuItem>
                <MenuItem value="On Hold">On Hold</MenuItem>
                <MenuItem value="Over Budget">Over Budget</MenuItem>
              </Select>
            </FormControl>
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleClose}>Cancel</Button>
          <Button onClick={handleSubmit} variant="contained">
            {editing ? 'Update' : 'Create'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default Projects;
